<template>
  <Layout :hideMobileMenu="true">
    <HeaderRoot title="Download" />

    <div class="book-export container">
      <BackLink v-if="!doc.isCurrentThread()" class="book-export__backlink" />
      <div class="book-export__container-wrapper">
        <section class="book-export__container">
          <div v-if="isLoading || !billing">
            <loading-spinner />
          </div>

          <div v-else>
            <p v-if="!view" class="book-export__subscription-info">
              The PDF summary of this book is currently not available. Please
              try again later!
            </p>
            <h1 v-else-if="view.isTrial()" class="book-export__title">
              End your free trial to download
            </h1>
            <h1 v-else-if="view.isFree()" class="book-export__title">
              Downloads only for full subscribers
            </h1>
            <h1 v-else-if="view.isPaidLimit()" class="book-export__title">
              Download limit reached
            </h1>
            <h1 v-else class="book-export__title">Download summary</h1>

            <div v-if="view">
              <div class="book-export__subscription-info">
                <div
                  v-if="view.isFree()"
                  class="book-export__subscription-info"
                >
                  <p class="book-export__additional-info">
                    Downloading is only allowed for Shortform subscribers.
                  </p>
                  <p>
                    Please start a free trial to get access to content
                    downloads.
                  </p>

                  <div class="book-export__start-trial-box">
                    <img
                      class="book-export__start-trial-image"
                      alt="Subscribe"
                      src="@/assets.app/images/books-download-subscribe.svg"
                    />
                    <div class="book-export__start-trial-header">
                      <p class="book-export__start-trial-title">
                        {{ boxText }}
                      </p>
                    </div>

                    <div>
                      <router-link
                        class="btns pdf-download__billing-link pdf-download__btn book-export__start-trial-button"
                        :to="{ name: 'billing' }"
                      >
                        Start free trial
                      </router-link>
                    </div>
                  </div>
                </div>
                <div v-if="view.isFreeCanDownload()">
                  <p class="book-export__subscription-info">
                    A free PDF summary of this book is available as a sample.
                    This PDF is representative of the formatting quality we have
                    for all of our PDF summaries. To access PDFs for all book
                    summaries,
                    <router-link
                      :to="{ name: 'billing' }"
                      class="pdf-download__billing-link"
                      >please sign up for a subscription</router-link
                    >.
                  </p>
                </div>
              </div>

              <div v-if="view.isTrial()" class="book-export__subscription-info">
                <p class="book-export__additional-info">
                  Full subscribers get {{ view.maxLimit() }} new downloads every
                  7 days.
                </p>
                <p v-if="billing.supportsCancelTrial">
                  You're currently in your free trial - please wait
                  <strong>{{ billing.daysUntilTrialEnd() }}</strong> for your
                  free trial to finish. You can read and listen to all books in
                  our app anytime.
                </p>
                <p v-else>
                  You're currently in your trial - please wait
                  <strong>{{ billing.daysUntilTrialEnd() }} </strong> for your
                  trial to finish or
                  <a
                    class="book-export__cancel-trial"
                    @click="showEndTrialPopup"
                    >End the trial now</a
                  >. You can read and listen to all books in our app anytime.
                </p>

                <img
                  class="book-export__download-image"
                  alt="Free trial"
                  src="@/assets.app/images/books-download-free-trial.svg"
                />
              </div>
              <BasePopup
                ref="popup"
                class="book-preview"
                title="End Trial Period"
              >
                <div class="popup__inner">
                  <p>
                    When you press Confirm below, you'll immediately get full
                    access to Shortform, including PDF downloads, and you'll be
                    charged according to the billing plan you selected.
                  </p>

                  <div class="book-preview__footer">
                    <button class="btns cancel-confirm" @click="cancelTrial">
                      Confirm
                    </button>
                    <button
                      class="btns btns--white cancel-cancel"
                      @click="hideEndTrialPopup"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </BasePopup>
              <p
                v-if="view.isTrialCanDownload()"
                class="book-export__subscription-info"
              >
                This PDF summary is available to download.
              </p>

              <div
                v-if="view.isPaidDownloaded()"
                class="book-export__subscription-info"
              >
                <p class="book-export__info">
                  Full subscribers can access {{ view.maxLimit() }} new PDF
                  downloads every 7 days.
                </p>
                <p class="book-export__additional-info">
                  You've previously accessed this PDF, so you can download it
                  again without it counting toward your limit.
                </p>
              </div>
              <div
                v-if="view.isPaidLimit()"
                class="book-export__subscription-info"
              >
                <BookExportLimit :seconds="view.nextDownloadSeconds()" />
              </div>

              <div
                v-if="view.isPaidCanDownload()"
                class="book-export__subscription-info"
              >
                <p class="book-export__info">
                  Full subscribers can access {{ view.maxLimit() }} new PDF
                  downloads every 7 days.
                </p>

                <div class="book-export__additional-info">
                  You currently have
                  <strong> {{ view.remainingDownloadsText() }} </strong>
                  remaining.
                </div>
              </div>

              <div v-if="view.canDownload()">
                <div class="book-export__book-info">
                  <span class="book-export__book-title">{{ doc.title }}</span>
                  <span class="book-export__book-author">{{ doc.author }}</span>
                </div>

                <section class="book-export__item book-export__pdf">
                  <h2 class="book-export__action-title">
                    <span
                      class="book-export__title-arrow iconfont iarrow"
                    ></span
                    >Download as PDF
                  </h2>

                  <p class="book-export__action-description">
                    Opens well on most devices. Has additional formatting for
                    readability.
                  </p>

                  <PdfDownloadLink class="pdf-download__button" :book="doc" />
                </section>

                <KindleExportForm :book="doc" />

                <section class="book-export__disclaimer">
                  <strong>Note</strong>: This document is restricted to your
                  personal use only. No part of this document may be reproduced
                  or distributed other than for personal use, nor shared with
                  individuals other than yourself, without prior written
                  permission of Shortform Inc.
                </section>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRoute } from 'vue-router'

import BasePopup from '@/components/ui/BasePopup.vue'
import PdfDownloadLink from './PdfDownloadLink.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Billing } from '@/models/interfaces'
import { BillingView } from '@/models/billing'
import { BookView } from '@/models/doc.book'
import { DocExportView } from '@/models/doc.export'
import Layout from '../Layout.vue'
import HeaderRoot from '@/app/components/header/Root.vue'
import KindleExportForm from '@/app/components/book/export/KindleExportForm.vue'
import BookExportLimit from '@/app/components/book/BookExportLimit.vue'
import BackLink from '@/app/components/BackLink.vue'
import { trialByPlan15535Treatment } from '@/services/ab/15535_trial_by_plan'

const props = defineProps<{
  doc: BookView
}>()

const route = useRoute()
const wait = useWait()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

const billing = ref<BillingView | null>(null)
const view = ref<DocExportView | null>(null)

onBeforeMount(async () => {
  wait.start('Loading Book Export View')
  const response = await backend.getBilling()
  await _initView(response)
  wait.end('Loading Book Export View')

  setTimeout(
    () => {
      // Reload the page once in 5 minutes to get new doc export token.
      // This is for the case the user opens the page and leaves it for some time
      // before using the download button.
      if (route.name === 'book.export') {
        window.location.reload()
      }
    },
    5 * 60 * 1000,
  )
})

async function _initView(billingData: Billing): Promise<void> {
  billing.value = new BillingView(billingData)
  if (!props.doc.doc_export_stats) {
    try {
      await props.doc.getDocExportStats()
    } catch (error: any) {
      Sentry.captureException(error)
    }
  }
  if (props.doc.doc_export_stats) {
    view.value = new DocExportView(props.doc, billing.value as BillingView)
  }
}

function showEndTrialPopup(): void {
  popup.value?.show()
}

function hideEndTrialPopup(): void {
  popup.value?.hide()
}

const boxText = computed(() => {
  return trialByPlan15535Treatment()
    ? "Try Shortform's free trial without limits."
    : "Try Shortform's 5-day free trial without limits."
})

/**
 * Cancel subscription handler (for modal dialog).
 */
async function cancelTrial(): Promise<void> {
  try {
    hideEndTrialPopup()
    wait.start('Loading Book Export View')
    const response = await backend.cancelTrial()
    await _initView(response)
  } catch (error) {
    Sentry.captureException(error)
    alert('Error canceling trial')
  } finally {
    wait.end('Loading Book Export View')
  }
}

const isLoading = computed(() => {
  return wait.is('Loading Book Export View')
})
</script>

<style lang="scss" scoped>
.book-export {
  &__backlink {
    margin-bottom: 24px;
  }
}
</style>
